<template>
  <v-dialog :value="value" @input="$emit('input', $event)" width="720" persistent no-click-animation>
    <v-card>
      <v-card-title class="pa-0">
        <v-toolbar flat height="55">
          <v-toolbar-title class="text--secondary subtitle-1 font-weight-medium">Acordar fecha de pago</v-toolbar-title>
          <v-spacer />
          <v-toolbar-items>
            <v-row align="center" justify="center" class="mr-1">
              <v-btn class="ml-3" @click="close" icon small><v-icon size="24">mdi-close</v-icon></v-btn>
            </v-row>
          </v-toolbar-items>
        </v-toolbar>
      </v-card-title>
      <v-divider />
      <v-card-text class="pa-0 background-card">
        <v-row class="mx-0 pt-4">
          <v-col class="py-0">
            <span class="d-block body-2">Si acordaste una fecha de pago con tu cliente puedes registrarla acá, y la cobranza de esos documentos pasará a seguir esa fecha en vez de su vencimiento.</span>
          </v-col>
        </v-row>
        <v-list class="v-list-form py-6 transparent">

          <!-- facturas -->
          <v-list-item class="py-2" v-if="!invoice">
            <v-list-item-title>Facturas</v-list-item-title>
            <v-list-item-subtitle>
              <v-col class="px-0">
                <template v-if="form.invoices.length">
                  <v-row class="mx-0 mb-1" align="center" v-for="i in form.invoices" :key="i.id">
                    <v-col class="py-1 px-0">
                      <span class="d-block body-2 text--secondary font-weight-medium">{{i.document.name}} Nº {{i.number}}</span>
                      <span class="d-block body-2">{{i.customer.name}} · {{i.total | currency(i.currency)}} {{i.currency}}</span>
                      <span class="d-block caption red--text" v-if="errorsByInvoice[i.id]">{{errorsByInvoice[i.id]}}</span>
                    </v-col>
                    <v-btn @click="remove(i)" icon small><v-icon color="secondary" size="20">mdi-close</v-icon></v-btn>
                  </v-row>
                </template>
                <v-btn class="mt-1" @click="dialogInvoice=true" outlined small>
                  <v-icon left small>mdi-plus</v-icon>{{form.invoices.length ? 'Agregar otras' : 'Seleccionar facturas'}}
                </v-btn>
                <span class="d-block mt-2 caption red--text" v-if="$v.form.invoices.$error">Selecciona al menos una factura</span>
              </v-col>
            </v-list-item-subtitle>
          </v-list-item>
          <!-- end facturas -->

          <!-- fecha -->
          <v-list-item class="py-2">
            <v-list-item-title>Fecha acordada de pago</v-list-item-title>
            <v-list-item-subtitle>
              <v-col cols="6" class="px-0">
                <v-menu v-model="datePickers.date" :close-on-content-click="false" transition="slide-y-transition" offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field v-model="$v.form.date.$model" :error="$v.form.date.$error" v-bind="attrs" v-on="on" background-color="white" outlined single-line dense readonly hide-details>
                      <template v-slot:append>
                        <v-icon class="mr-3" small color="secondary">mdi-calendar</v-icon>
                      </template>
                    </v-text-field>
                  </template>
                  <v-date-picker v-model="$v.form.date.$model" @input="datePickers.date=false" type="date" :min="today" :first-day-of-week="1" locale="es-cl" width="280" color="primary" no-title />
                </v-menu>
              </v-col>
            </v-list-item-subtitle>
          </v-list-item>
          <!-- end fecha -->

          <!-- nota -->
          <v-list-item class="py-2">
            <v-list-item-title>
              Nota interna <v-chip class="ml-1" color="defaultGrey" text-color="secondary" x-small>Opcional</v-chip>
            </v-list-item-title>
            <v-list-item-subtitle>
              <v-textarea v-model="$v.form.note.$model" :error="$v.form.note.$error" background-color="white" outlined rows="2" maxlength="256" counter hide-details="auto" placeholder="Ej: Pago acordado para 30 días más." />
            </v-list-item-subtitle>
          </v-list-item>
          <!-- end nota -->

        </v-list>
      </v-card-text>
      <v-divider />
      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn @click="close" :disabled="saving" outlined>Cancelar</v-btn>
        <v-btn @click="save" :loading="saving" color="primary">Acordar fecha</v-btn>
      </v-card-actions>
    </v-card>

    <invoice-picker-dialog v-model="dialogInvoice" :selected="form.invoices" @select="add" />

  </v-dialog>
</template>
<script>
import { maxLength, minLength, required } from 'vuelidate/lib/validators'

import InvoicePickerDialog from '@/components/receivables/InvoicePickerDialog'

import GenericViewMixin from '@/mixins/GenericViewMixin'

const defaultForm = () => ({
  date: '',
  invoices: [],
  note: ''
})

export default {
  components: {
    InvoicePickerDialog
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    invoice: {
      type: Object,
      default: null
    }
  },
  mixins: [
    GenericViewMixin
  ],
  data: () => ({
    datePickers: { date: false },
    dialogInvoice: false,
    errorsByInvoice: {},
    form: defaultForm(),
    saving: false
  }),
  watch: {
    value (open) {
      if (open) {
        this.reset()
      }
    }
  },
  methods: {
    reset () {
      this.form = defaultForm()
      this.errorsByInvoice = {}
      this.saving = false

      if (this.invoice) {
        this.form.invoices = [this.invoice]
      }

      this.$v.$reset()
    },
    add (invoices) {
      const ids = this.form.invoices.map(i => i.id)
      this.form.invoices = this.form.invoices.concat(invoices.filter(i => !ids.includes(i.id)))
    },
    remove (invoice) {
      this.form.invoices = this.form.invoices.filter(i => i.id !== invoice.id)
      this.$delete(this.errorsByInvoice, invoice.id)
    },
    async save () {
      this.$v.form.$touch()

      if (this.$v.form.$invalid) {
        return false
      }

      this.saving = true
      this.errorsByInvoice = {}

      const failed = []
      let created = 0

      // secuencial a propósito: TOGGLE_CREATE_LOADER en store/abstract.js es un
      // flip y no un contador, así que N CREATE en paralelo dejan createLoader
      // invertido a mitad de camino
      for (const invoice of this.form.invoices) {
        try {
          await this.$store.dispatch('receivables/CREATE', {
            resource: 'agreements',
            payload: {
              invoice: invoice.id,
              date: this.form.date,
              note: this.form.note || null
            },
            // SET_DETAIL empujaría cada respuesta a agreementsList, saltándose
            // el filtro, el orden y la página activos
            commitInstance: false
          })
          created += 1
        } catch (error) {
          failed.push({ invoice: invoice, message: this.parseErrors(error.response.data) })
        }
      }

      this.saving = false
      this.$emit('created')

      if (!failed.length) {
        this.$dialog.message.success(created === 1 ? 'El acuerdo ha sido registrado' : `Se registraron ${created} acuerdos`)
        this.close()
        return true
      }

      // los que sí se crearon quedan; la modal se queda abierta solo con los
      // que fallaron para que reintentar no duplique nada
      this.errorsByInvoice = failed.reduce((acc, f) => Object.assign(acc, { [f.invoice.id]: f.message }), {})
      this.form.invoices = failed.map(f => f.invoice)
      this.$dialog.message.error(`Se registraron ${created} de ${created + failed.length} acuerdos. Revisa las facturas marcadas.`)
    },
    close () {
      this.$emit('input', false)
    }
  },
  validations: {
    form: {
      date: { required },
      invoices: { required, minLength: minLength(1) },
      note: { maxLength: maxLength(256) }
    }
  }
}
</script>
